import React, { useEffect } from 'react';
import { Typography, QuickFilterButton, Link } from '@hpi-sox/hpstellar-core';
import {
    Typography as TypographyVisId,
    QuickFilterButton as QuickFilterButtonVisId,
    Button,
} from '@hpi-sox/hpstellar-core/visId';
import { ChevronRight } from '@hpi-sox/hpstellar-veneer-icons';

import withPageComponents from '../../../page/components/with-page-components';
import useTemplateTheme from '../../../hooks/useTemplateTheme';
import usePage from '../../../hooks/usePage';
import { selectRecommendedConfig, verifyPicks } from '../../util';
import ETRWCSApi from '../../../core/src/etr-wcs-api-client';
import { onEnterClick } from '../../../ui/components/accessible-element';
import useInpChecked from '../../../hooks/useInpChecked';

import './recommended-configurations.less';

const fetchCompare = (catId, updateComponent) => {
    return ETRWCSApi.cto
        .compare(catId)
        .then(resp => {
            updateComponent({ compareConfig: resp });
        })
        .catch(e => {
            updateComponent({ compareConfig: [] });
        });
};

const handleSelection = (updateComponent, configCatentryId) => value => {
    updateComponent({ configCatentryId }, state => {
        const { components } = state;
        const { baseConfig = {} } = components || {};
        const { specCategories, recommendedConfigs } = baseConfig;

        selectRecommendedConfig(specCategories, recommendedConfigs, configCatentryId);
        //verify selection against server
        verifyPicks(components, updateComponent);
        //add flag to for verifying to prevent extra price and shiptdate api calls
        return { components: { ...components, verifyingPicks: true } };
    });
};

const RecommendedConfiguration = ({ configName, onClick, isActive, configKey, v2, ...rest }) => {
    const inpCheckedData = useInpChecked(configKey, isActive);
    const { derivedCheck, setInpChecked } = inpCheckedData || {};

    const onClickHandler = e => {
        try {
            if (typeof setInpChecked === 'function' && !isActive && typeof derivedCheck !== 'boolean') {
                setInpChecked(!isActive);
            }
        } catch (e) {}

        try {
            setTimeout(() => {
                onClick();
            }, 0);
        } catch (e) {}
    };

    const derivedIsActive = typeof derivedCheck === 'boolean' ? derivedCheck : isActive;
    return v2 ? (
        <QuickFilterButtonVisId
            {...rest}
            className="config-quick-filter-visId"
            isActive={derivedIsActive}
            onClick={onClickHandler}
            gtmActions={rest.gtmActions}
        >
            {configName}
        </QuickFilterButtonVisId>
    ) : (
        <QuickFilterButton {...rest} isActive={derivedIsActive} onClick={onClickHandler}>
            {configName}
        </QuickFilterButton>
    );
};

export default withPageComponents(
    ({
        baseConfig = {},
        compareConfig,
        translations = { titleText: 'Recommended Configurations' },
        toggleCompare,
        device,
        v2,
    }) => {
        const { updateComponent } = usePage();
        const { typography } = useTemplateTheme(['typography']);
        const { primaryButtonLinkClassName } = typography || {};
        const { titleText } = translations;
        const { recommendedConfigs, catentryId } = baseConfig;
        const { ctaCompareLink, ctaCompareText, ...configs } = recommendedConfigs || {};
        const { gtmAttributes } = compareConfig || {};
        useEffect(() => {
            if (configs && Object.keys(configs).length > 1 && !compareConfig && catentryId) {
                fetchCompare(catentryId, updateComponent);
            }
        }, [JSON.stringify(configs)]);
        if (!configs || Object.keys(configs).length < 2 || device === 'mobile') {
            return null;
        }

        return (
            <div className={`recommended-configs ${v2 ? 'recommended-configs--v2' : ''}`}>
                {v2 ? (
                    <TypographyVisId tag="h2" variant="h4" className="section-title">
                        {titleText}
                    </TypographyVisId>
                ) : (
                    <Typography tag="h2" variant="bodyL" className="section-title">
                        {titleText}
                    </Typography>
                )}
                <div className="recommended-configs-container">
                    <div className="quick-filter-container">
                        {Object.keys(configs).map((configId, idx) => {
                            const config = recommendedConfigs[configId];
                            const { configName, selected, configCatentryId, gtmActions } = config;
                            const key = `${configName}-${idx}`;
                            return (
                                <RecommendedConfiguration
                                    key={key}
                                    configKey={key}
                                    className="config-quick-filter"
                                    isActive={selected === 'yes'}
                                    onClick={handleSelection(updateComponent, configCatentryId)}
                                    gtmActions={gtmActions}
                                    configName={configName}
                                    v2={v2}
                                />
                            );
                        })}
                        {v2 && (
                            <Button
                                className="recommended-configs--v2_view-details"
                                variation="tertiary"
                                tag="a"
                                endIcon={<ChevronRight size={16} />}
                                onClick={e => {
                                    e.preventDefault();
                                    toggleCompare(e);
                                }}
                                tabIndex="0"
                                onKeyUp={onEnterClick(toggleCompare)}
                                {...(gtmAttributes || {})}
                            >
                                {ctaCompareText}
                            </Button>
                        )}
                    </div>
                    {!v2 && compareConfig && compareConfig.length > 1 && (
                        <Link
                            className={primaryButtonLinkClassName}
                            onClick={e => {
                                e.preventDefault();
                                toggleCompare(e);
                            }}
                            tabIndex="0"
                            onKeyUp={onEnterClick(toggleCompare)}
                            {...(gtmAttributes || {})}
                        >
                            {ctaCompareText}
                        </Link>
                    )}
                </div>
            </div>
        );
    },
    { components: ['baseConfig', 'compareConfig'], siteConfig: true, device: true },
);
